import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../components/Icon';
import { contactInfo } from '../mock';
import aboutBanner from '../assets/brand/about-banner.png';

const values = [
  { icon: 'shieldCheck', title: 'Integrity First', description: 'No hidden fees, no false promises — every candidate and partner gets the same honest process.' },
  { icon: 'heart', title: 'Candidate-Centric', description: 'We measure success by the careers we help build, not just the placements we close.' },
  { icon: 'globe', title: 'Global Reach', description: 'From India to Europe, we bridge opportunity across borders with local, on-the-ground support.' },
  { icon: 'users', title: 'Partnership Driven', description: 'Agencies and agents are core to how we scale — their success is built into our model.' },
  { icon: 'target', title: 'Precision Matching', description: 'We match skills to roles carefully, so placements last and candidates thrive.' },
  { icon: 'rocket', title: 'Always Growing', description: 'New countries, new roles, new partnerships — we expand where the opportunity is real.' },
];

const DarkBand = ({ children }) => (
  <section className="relative overflow-hidden px-6 text-center" style={{ padding: '96px 24px', background: '#2d2a24', color: '#fff' }}>
    <span style={{ position: 'absolute', left: -110, top: -110, width: 360, height: 360, border: '1px solid #e08e0b', borderRadius: '50%', opacity: 0.16, pointerEvents: 'none' }} />
    <span style={{ position: 'absolute', right: -90, bottom: -130, width: 320, height: 320, border: '1px solid #fff', borderRadius: '50%', opacity: 0.06, pointerEvents: 'none' }} />
    <div className="relative">{children}</div>
  </section>
);

const AboutUs = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen" style={{ background: 'var(--zg-bg)' }}>
      {/* Hero */}
      <section className="px-6" style={{ padding: '128px 24px 56px', background: 'var(--zg-surface)' }}>
        <div className="mx-auto max-w-3xl">
          <span className="inline-block text-white text-xs rounded-full px-4 py-1.5 mb-6" style={{ background: '#2d2a24' }}>
            About Zeevogo
          </span>
          <h1 className="font-bold mb-6 leading-tight" style={{ fontSize: 60, color: 'var(--zg-ink)' }}>
            Connecting Ambition With Opportunity
          </h1>
          <p className="text-xl leading-relaxed" style={{ color: 'var(--zg-muted)' }}>
            Zeevogo exists to close one gap: the distance between real, ready talent in India and the genuine
            job opportunities waiting across Europe. We built a transparent, end-to-end pipeline — verified
            employers, honest visa guidance, and a partner network of agencies and agents — so a good career
            abroad depends on your skill and effort, not on who you happen to know or which agent you got
            lucky with.
          </p>
        </div>
      </section>

      {/* Banner */}
      <section className="px-6" style={{ padding: '0 24px 64px', background: 'var(--zg-surface)' }}>
        <div className="mx-auto max-w-5xl">
          <img
            src={aboutBanner}
            alt="Zeevogo — Every route, one platform."
            className="w-full h-auto rounded-[18px] border"
            style={{ borderColor: 'var(--zg-border)', display: 'block' }}
          />
        </div>
      </section>

      {/* Our Story */}
      <DarkBand>
        <div className="mx-auto max-w-3xl">
          <h2 className="text-5xl font-bold mb-6">Our Story</h2>
          <p className="text-xl leading-relaxed" style={{ color: '#b4ae9f' }}>
            Zeevogo started with a pattern we kept seeing repeat itself: a capable electrician, a trained driver,
            a hospitality graduate — each one ready to work, each one blocked not by a lack of skill but by a
            maze of unlicensed agents, inflated "processing fees," and vague promises that rarely became real
            jobs. Families were saving for years, sometimes borrowing against land or gold, to fund a move
            abroad that too often fell through before a visa was ever stamped. We built Zeevogo to replace that
            maze with a straight line: verified employer partners across Poland, Romania, Croatia and beyond, a
            fixed and transparent process from application to visa, and a support system that stays with every
            candidate long after they've landed.
          </p>
        </div>
      </DarkBand>

      {/* Narrative for candidates from developing economies */}
      <section className="px-6" style={{ padding: '80px 24px' }}>
        <div className="mx-auto max-w-3xl">
          <h2 className="text-4xl font-bold mb-6" style={{ color: 'var(--zg-ink)' }}>
            Built For The Leap You're About To Take
          </h2>
          <div className="space-y-5 text-lg leading-relaxed" style={{ color: 'var(--zg-ink2)' }}>
            <p>
              Choosing to build a career outside your home country is rarely just a job decision — it's a
              decision made for a family, a future, sometimes an entire village. It usually comes with real
              risk: money paid upfront, months of waiting, and a language and culture you're stepping into
              without a safety net.
            </p>
            <p>
              We know, because most of the people who use Zeevogo are taking that exact leap — often as the
              first in their family to work in Europe, coming from towns and cities across India where a
              European salary can change what's possible for an entire household, not just one person.
            </p>
            <p>
              That's why every listing on this platform is tied to a real, verified employer. Every stage of
              your application is visible to you in real time through our tracker — no wondering, no
              radio silence for weeks at a stretch. And every partner agency or agent in our network is held
              to the same standard: no hidden fees, no false promises, and no candidate left guessing where
              their file stands.
            </p>
            <p style={{ color: 'var(--zg-ink)', fontWeight: 600 }}>
              Your ambition got you this far. Our job is to make sure the system doesn't fail you the rest of
              the way.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="px-6" style={{ padding: '80px 24px', background: 'var(--zg-surface)' }}>
        <div className="mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-4" style={{ color: 'var(--zg-ink)' }}>
              What We Stand For
            </h2>
            <p className="text-xl max-w-2xl mx-auto" style={{ color: 'var(--zg-muted)' }}>
              The principles that guide every placement, partnership, and product decision we make
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value) => (
              <div
                key={value.title}
                className="rounded-[18px] border p-6 transition-all"
                style={{ borderColor: 'var(--zg-border)', background: 'var(--zg-card)' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#e08e0b';
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 18px 40px rgba(45,42,36,.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--zg-border)';
                  e.currentTarget.style.transform = 'none';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div
                  className="w-[54px] h-[54px] rounded-2xl flex items-center justify-center mb-4"
                  style={{ background: 'rgba(224,142,11,.12)', color: '#c67e0a' }}
                >
                  <Icon name={value.icon} size={26} />
                </div>
                <div className="text-xl font-bold mb-2" style={{ color: 'var(--zg-ink)' }}>
                  {value.title}
                </div>
                <p style={{ color: 'var(--zg-muted)' }}>{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <DarkBand>
        <div className="mx-auto max-w-3xl">
          <h2 className="text-5xl font-bold mb-6">Want to Work With Us?</h2>
          <p className="text-xl mb-8" style={{ color: '#b4ae9f' }}>
            Whether you're looking for a job, a partnership, or just have questions — we'd love to hear from you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`mailto:${contactInfo.email}`}
              className="inline-flex items-center h-14 px-8 rounded-full text-base font-medium no-underline transition-all"
              style={{ background: 'var(--zg-card)', color: 'var(--zg-ink)' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 12px 26px rgba(0,0,0,.22)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              Email: {contactInfo.email}
            </a>
            <Link
              to="/partnership/agent"
              className="inline-flex items-center h-14 px-8 rounded-full text-base font-medium no-underline border border-white text-white"
            >
              Explore Partnerships
            </Link>
          </div>
        </div>
      </DarkBand>
    </div>
  );
};

export default AboutUs;
